import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, FileText, AlertCircle, Loader2 } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { LoadingDialog } from "@/components/ui/loading-dialog";

// Removed old ParseResult import; initial upload returns a run response

interface FileUploadSectionProps {
  isUploading: boolean;
  uploadError: string | null;
  session: any;
  setIsUploading: (value: boolean) => void;
  setUploadError: (value: string | null) => void;
  setParseResult: (result: any) => void;
}

export const FileUploadSection = ({
  isUploading,
  uploadError,
  session,
  setIsUploading,
  setUploadError,
  setParseResult,
}: FileUploadSectionProps) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setSelectedFile(file);
      setUploadError(null);

      // Automatically trigger upload when file is selected
      await handleUpload(file);
    }
  };

  const handleUpload = async (file: File) => {
    if (!file) {
      setUploadError("Please select a file to upload");
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      // Upload file to Supabase Storage
      const fileExt = file.name.split(".").pop();
      const fileName = `${Date.now()}_${Math.random()
        .toString(36)
        .substring(2, 15)}.${fileExt}`;
      const filePath = `${session?.user?.id}/opportunities/${fileName}`;

      await supabase.auth.setSession({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      });

      // Upload file to Supabase storage with progress tracking
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from("nonprofit-cloud")
        .upload(filePath, file);

      if (uploadError) {
        throw new Error(uploadError.message);
      }

      // Get public URL for the uploaded file
      const { data: urlData } = supabase.storage
        .from("nonprofit-cloud")
        .getPublicUrl(filePath);

      if (!urlData?.publicUrl) {
        throw new Error("Failed to get public URL for uploaded file");
      }

      // Send file to API for parsing
      const response = await fetch(
        `${
          import.meta.env.VITE_CC_API_URL
        }/v1/opportunities/catalog/parse-from-docs`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            document_url: urlData.publicUrl,
            document_type: "opportunity",
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `API error (${response.status}): ${errorText || response.statusText}`
        );
      }

      const result = await response.json();
      setParseResult(result);
    } catch (error) {
      console.error("Error uploading or parsing document:", error);
      setUploadError(
        error instanceof Error
          ? error.message
          : "An unknown error occurred during upload"
      );
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      setSelectedFile(null);
    }
  };

  return (
    <>
      <LoadingDialog
        open={isUploading}
        title="Processing.."
        description="might take upto a minute, do not close this tab"
      />
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <div>
              <Label htmlFor="file-upload">Upload Opportunity Document</Label>
              <div className="mt-2">
                <Input
                  id="file-upload"
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.txt,.xlsx,.xls"
                  onChange={handleFileChange}
                  disabled={isUploading}
                />
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Supported formats: PDF, Word, Text, Excel
              </p>
              {uploadError && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Error</AlertTitle>
                  <AlertDescription>{uploadError}</AlertDescription>
                </Alert>
              )}
            </div>

            <div className="flex justify-end space-x-2">
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />{" "}
                    Processing...
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" /> Select & Upload Document
                  </>
                )}
              </Button>
            </div>

            <div className="text-sm text-muted-foreground mt-4">
              <div className="flex items-start gap-2">
                <FileText className="h-4 w-4 mt-0.5" />
                <div>
                  <p className="font-medium">Document Requirements:</p>
                  <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                    <li>
                      Include opportunity name, description, and format details
                    </li>
                    <li>
                      Specify pricing information (per volunteer or per event)
                    </li>
                    <li>
                      List minimum and maximum volunteer numbers for each
                      opportunity
                    </li>
                    <li>
                      Provide any additional fees or geographic pricing details
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  );
};
